import { type SECRPriorYearOverrides } from '@shared/api/types';
import Unit from '@shared/components/content/unit';
import InputFormField from '@shared/components/form/input-form-field';
import { cn, formatNumber } from '@shared/lib/utils';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import {
  ALL_EMISSION_KEYS,
  EMISSION_SCOPES,
  TABLE_INPUT_CLASS,
} from '../constants';

const formatKwh = (
  kwh: number | null | undefined,
  tonnes: number | null | undefined
): string => {
  if (kwh != null) return formatNumber(kwh);
  if (tonnes === 0) return formatNumber(0);
  return '-';
};
import {
  type CurrentYearData,
  type EmissionRowKey,
  type PrevSource,
  type SecrFormValues,
} from '../schema';

interface SECRTableRowProps {
  label: string;
  icon?: React.ReactNode;
  name: string;
  source: PrevSource;
  prevYearTonnes?: number | null;
  prevYearKwh?: number | null;
  currentTco2e?: number | null;
  currentKwh?: number | null;
}

function SECRTableRow({
  label,
  icon,
  name,
  source,
  prevYearTonnes,
  prevYearKwh,
  currentTco2e,
  currentKwh,
}: SECRTableRowProps) {
  const prevSection =
    source === 'none' ? null : source === 'my_emissions' ? (
      <>
        <td className="border-l border-primary-100 px-3 py-2.5 text-right text-sm">
          {prevYearTonnes != null ? formatNumber(prevYearTonnes) : '-'}
        </td>
        <td className="px-3 py-2.5 text-right text-sm">
          {formatKwh(prevYearKwh, prevYearTonnes)}
        </td>
      </>
    ) : (
      <>
        <td className="border-l border-primary-100 p-0">
          <InputFormField
            name={`${name}.prevYear`}
            type="number"
            placeholder="Add value"
            inputClassName={TABLE_INPUT_CLASS}
            className="p-0"
          />
        </td>
        <td className="p-0">
          <InputFormField
            name={`${name}.prevYearKwh`}
            type="number"
            placeholder="Add value"
            inputClassName={TABLE_INPUT_CLASS}
            className="p-0"
          />
        </td>
      </>
    );

  return (
    <tr className="border-b transition-colors last:border-0">
      <td className="border-r border-primary-100 px-3 py-2.5">
        <div className="flex items-center gap-2">
          {icon && <span className="shrink-0">{icon}</span>}
          <span className="text-sm">{label}</span>
        </div>
      </td>
      {prevSection}
      <td className="border-l border-teal-100 px-3 py-2.5 text-right text-sm text-muted-foreground">
        {currentTco2e != null ? formatNumber(currentTco2e) : '-'}
      </td>
      <td className="px-3 py-2.5 text-right text-sm text-muted-foreground">
        {formatKwh(currentKwh, currentTco2e)}
      </td>
    </tr>
  );
}

function SECRScopeHeader({
  label,
  colSpan = 5,
}: {
  label: string;
  colSpan?: number;
}) {
  return (
    <tr className="bg-muted/40 border-b">
      <td
        colSpan={colSpan}
        className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground"
      >
        {label}
      </td>
    </tr>
  );
}

interface SECRTableViewProps {
  source: PrevSource;
  prevYearLabel: string;
  currentYearLabel: string;
  currentYearData?: CurrentYearData;
  priorYearComputed?: SECRPriorYearOverrides | null;
}

export function SECRTableView({
  source,
  prevYearLabel,
  currentYearLabel,
  currentYearData,
  priorYearComputed,
}: SECRTableViewProps) {
  const { watch } = useFormContext<SecrFormValues>();
  const formValues = watch();

  const hasCurrentData = currentYearData != null;

  const sumDefined = (
    pick: (key: EmissionRowKey) => number | null | undefined
  ): number | null => {
    let sum = 0;
    let hasAny = false;
    for (const key of ALL_EMISSION_KEYS) {
      const value = pick(key);
      if (value != null) {
        sum += value;
        hasAny = true;
      }
    }
    return hasAny ? sum : null;
  };

  const currentTotalTco2e = hasCurrentData
    ? sumDefined((key) => currentYearData[key].tco2e)
    : null;
  const currentTotalKwh = hasCurrentData
    ? sumDefined((key) => currentYearData[key].kwh)
    : null;

  const prevTotalTco2e =
    source === 'none'
      ? null
      : source === 'my_emissions'
        ? (priorYearComputed?.totalTonnes ?? null)
        : ALL_EMISSION_KEYS.reduce(
            (sum, key: EmissionRowKey) =>
              sum + (Number(formValues[key]?.prevYear) || 0),
            0
          );

  const prevTotalKwh =
    source === 'none'
      ? null
      : source === 'my_emissions'
        ? (priorYearComputed?.totalKwh ?? null)
        : ALL_EMISSION_KEYS.reduce(
            (sum, key: EmissionRowKey) =>
              sum + (Number(formValues[key]?.prevYearKwh) || 0),
            0
          );

  const isOneYear = source === 'none';

  return (
    <div
      className={cn(
        'overflow-hidden rounded-lg border bg-gray-00 shadow-sm',
        isOneYear && 'w-[71%]'
      )}
    >
      <table className="w-full table-fixed border-collapse">
        <colgroup>
          <col className={isOneYear ? 'w-[59.15%]' : 'w-[42%]'} />
          {!isOneYear && <col className="w-[14.5%]" />}
          {!isOneYear && <col className="w-[14.5%]" />}
          <col className={isOneYear ? 'w-[20.42%]' : 'w-[14.5%]'} />
          <col className={isOneYear ? 'w-[20.42%]' : 'w-[14.5%]'} />
        </colgroup>
        <thead>
          <tr className="border-b">
            <th className="border-r border-primary-100 bg-primary-50 p-3 text-left text-sm font-semibold">
              Category
            </th>
            {!isOneYear && (
              <th
                colSpan={2}
                className="border-l border-primary-100 bg-teal-50 p-3 text-center text-sm font-semibold"
              >
                {prevYearLabel}
              </th>
            )}
            <th
              colSpan={2}
              className="border-l border-teal-100 bg-teal-100 p-3 text-center text-sm font-semibold"
            >
              {currentYearLabel}
            </th>
          </tr>
          <tr className="border-b text-xs">
            <th className="border-r border-primary-100 px-3 py-2" />
            {!isOneYear && (
              <>
                <th className="whitespace-nowrap border-l border-primary-100 px-3 py-2 text-right font-medium">
                  Emissions <Unit variant="tCO2e" className="ml-0.5 inline" />
                </th>
                <th className="whitespace-nowrap px-3 py-2 text-right font-medium">
                  Energy <Unit variant="kWh (net)" className="ml-0.5 inline" />
                </th>
              </>
            )}
            <th className="whitespace-nowrap border-l border-teal-100 px-3 py-2 text-right font-medium">
              Emissions <Unit variant="tCO2e" className="ml-0.5 inline" />
            </th>
            <th className="whitespace-nowrap px-3 py-2 text-right font-medium">
              Energy <Unit variant="kWh (net)" className="ml-0.5 inline" />
            </th>
          </tr>
        </thead>

        <tbody>
          {EMISSION_SCOPES.map(({ label, rows }) => (
            <React.Fragment key={label}>
              <SECRScopeHeader
                label={label.split(' — ')[0]}
                colSpan={isOneYear ? 3 : 5}
              />
              {rows.map(({ key, label: rowLabel, icon }) => {
                const priorCategory = priorYearComputed?.categories[key];
                return (
                  <SECRTableRow
                    key={key}
                    label={rowLabel}
                    icon={icon}
                    name={key}
                    source={source}
                    prevYearTonnes={priorCategory?.tonnes}
                    prevYearKwh={priorCategory?.kwh}
                    currentTco2e={
                      hasCurrentData ? currentYearData![key].tco2e : null
                    }
                    currentKwh={
                      hasCurrentData ? currentYearData![key].kwh : null
                    }
                  />
                );
              })}
            </React.Fragment>
          ))}
        </tbody>

        <tfoot className="border-t-2">
          <tr>
            <td className="border-r border-primary-100 px-3 py-2.5 text-right text-sm font-semibold">
              Total
            </td>
            {!isOneYear && (
              <>
                <td className="border-l border-primary-100 px-3 py-2.5 text-right text-sm font-semibold">
                  {prevTotalTco2e != null ? formatNumber(prevTotalTco2e) : '-'}
                </td>
                <td className="px-3 py-2.5 text-right text-sm font-semibold">
                  {formatKwh(prevTotalKwh, prevTotalTco2e)}
                </td>
              </>
            )}
            <td className="border-l border-teal-100 px-3 py-2.5 text-right text-sm font-semibold">
              {currentTotalTco2e != null
                ? formatNumber(currentTotalTco2e)
                : '-'}
            </td>
            <td className="px-3 py-2.5 text-right text-sm font-semibold">
              {formatKwh(currentTotalKwh, currentTotalTco2e)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
